import { Container, ContainerChild, Graphics, Sprite, Texture } from "pixi.js";

import { CrawlService } from "../Services/CrawlService";

import {
  renderCrawlChromeSvg,
  renderCrawlScrollSvg,
  type CrawlDesign,
} from "../../../../data/crawlDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/** Shared canvas context for measuring the scroll segment's natural width. */
let measureCtx: CanvasRenderingContext2D | null = null;
const measureSegment = (design: CrawlDesign, text: string): number => {
  if (typeof document === "undefined") return design.cardWidth;
  if (!measureCtx) {
    measureCtx = document.createElement("canvas").getContext("2d");
  }
  if (!measureCtx) return design.cardWidth;
  const s = design.scroll;
  // Cap to the same 240 chars the catalog's scrollSegment caps to, so the
  // measured width matches the (capped) rastered strip AND a hostile
  // multi-thousand-char message can't blow up the bitmap size (OOM).
  const seg = `${(text || "•").slice(0, 240)}    ${s.sep}    `;
  measureCtx.font = `${s.fontStyle ?? "normal"} ${s.weight ?? "400"} ${s.fontSize}px ${s.font}`;
  const w = measureCtx.measureText(seg).width;
  // canvas measureText ignores letter-spacing — add it back approximately.
  const ls = (s.letterSpacing ?? 0) * seg.length;
  return Math.max(1, w + ls);
};

/**
 * Design-store render strategy for the Crawl widget. A crawl SCROLLS, so the
 * design is drawn as TWO rastered sprites:
 *  - the FIXED chrome (bar bg + tag) stretched to the widget bounds, and
 *  - the SCROLL text strip (the user's text in the design's typography + a
 *    coloured separator), masked to the design's clip region and animated
 *    horizontally by the same `CrawlService` rAF loop the legacy crawl uses
 *    (so speed + the velocity options stay consistent).
 *
 * Stateless like the legacy `CrawlWidgetRenderer`: `render()` returns a teardown
 * function the caller stashes on the container (`_crawlTicker`) and invokes on
 * the next render / removal.
 */
export class CrawlDesignRenderer {
  constructor(private readonly design: CrawlDesign) {}

  /** Build + animate the design into `container`; returns a teardown fn. */
  render(
    container: Container,
    destination: { width: number; height: number },
    text: string,
    velocity: string,
  ): () => void {
    const design = this.design;
    const destW = Math.max(1, destination.width);
    const destH = Math.max(1, destination.height);
    const scaleX = destW / design.cardWidth;
    const scaleY = destH / design.cardHeight;
    const renderScale = Math.min(Math.max(1, scaleX, scaleY), 3);

    let destroyed = false;
    let chromeTex: Texture | null = null;
    let scrollTex: Texture | null = null;

    // ── Chrome sprite (fixed, stretched to bounds) ──
    const chromeSprite = new Sprite(Texture.EMPTY);
    chromeSprite.width = destW;
    chromeSprite.height = destH;
    chromeSprite.position.set(0, 0);
    container.addChild(chromeSprite as unknown as ContainerChild);

    // ── Scroll strip (animated, masked to the clip region) ──
    const s = design.scroll;
    const clipXw = (s.clipX - design.barLeft) * scaleX;
    const clipYw = (s.clipY - design.barTop) * scaleY;
    const clipWw = Math.max(1, s.clipW * scaleX);
    const clipHw = Math.max(1, s.clipH * scaleY);

    const scrollLayer = new Container();
    container.addChild(scrollLayer);

    const clipMask = new Graphics();
    clipMask.rect(clipXw, clipYw, clipWw, clipHw).fill(0xffffff);
    scrollLayer.addChild(clipMask);
    scrollLayer.mask = clipMask;

    const segW = measureSegment(design, text);
    const spriteW = Math.max(1, segW * scaleX);

    const scrollSprite = new Sprite(Texture.EMPTY);
    scrollSprite.width = spriteW;
    scrollSprite.height = destH;
    scrollSprite.position.set(clipXw + clipWw, 0);
    scrollLayer.addChild(scrollSprite as unknown as ContainerChild);

    // Async raster of both layers. Fonts first so the text strip bakes the real
    // typeface (the chrome can decode immediately).
    void rasterizeSvgToTexture(
      renderCrawlChromeSvg(design),
      design.cardWidth,
      design.cardHeight,
      renderScale,
    )
      .then((tex) => {
        if (destroyed) {
          tex.destroy(true);
          return;
        }
        chromeTex = tex;
        chromeSprite.texture = tex;
        chromeSprite.width = destW;
        chromeSprite.height = destH;
      })
      .catch(() => {});

    void ensureDesignFontsReady(design.fonts).then(() => {
      if (destroyed) return;
      void rasterizeSvgToTexture(
        renderCrawlScrollSvg(design, text, segW),
        segW,
        design.cardHeight,
        renderScale,
      )
        .then((tex) => {
          if (destroyed) {
            tex.destroy(true);
            return;
          }
          scrollTex = tex;
          scrollSprite.texture = tex;
          scrollSprite.width = spriteW;
          scrollSprite.height = destH;
        })
        .catch(() => {});
    });

    // ── Animation: reuse CrawlService rAF + the legacy speed/loop math, but
    //   travel over the clip region instead of the full bar. The px/ms speed
    //   ends up identical to the legacy crawl (the cardWidth/clipW factor
    //   cancels), so the velocity options feel the same. ──
    const speed =
      CrawlService.SPEEDS[velocity as keyof typeof CrawlService.SPEEDS] ??
      CrawlService.SPEEDS.medium;
    const ratio = segW / s.clipW; // strip width relative to the clip width
    const cycleLen = 1 + ratio;
    const rateFactor = design.cardWidth / s.clipW;

    const crawlService = CrawlService.getInstance();

    // Idempotent teardown: unsubscribe the rAF tick + free both textures.
    // Called either by the caller (via container._crawlTicker on the next
    // render/removal) OR by the tick itself if it detects the sprites were
    // destroyed under it (see below).
    const teardown = () => {
      if (destroyed) return;
      destroyed = true;
      try {
        crawlService.unsubscribe(tick);
      } catch {
        /* best-effort */
      }
      if (chromeTex && chromeTex !== Texture.EMPTY) {
        try {
          chromeTex.destroy(true);
        } catch {
          /* best-effort */
        }
      }
      if (scrollTex && scrollTex !== Texture.EMPTY) {
        try {
          scrollTex.destroy(true);
        } catch {
          /* best-effort */
        }
      }
      chromeTex = null;
      scrollTex = null;
    };

    const tick = (time: number) => {
      if (destroyed) return;
      // The WidgetsFactory rebuilds a fresh container on every re-render (and
      // on removal), destroying our sprites WITHOUT calling our teardown (the
      // legacy `_crawlTicker` self-cleanup only fires if the SAME container is
      // re-rendered, which it isn't). Detect the destroyed sprite and self-
      // unsubscribe so a dead tick can't animate freed objects or leak the rAF.
      if (scrollSprite.destroyed || chromeSprite.destroyed) {
        teardown();
        return;
      }
      const progress =
        ((time * speed) / CrawlService.REFERENCE_WIDTH) * rateFactor;
      const p = progress % cycleLen;
      scrollSprite.x = clipXw + (1 - p) * clipWw;
    };

    crawlService.subscribe(tick);

    return teardown;
  }
}
